import { Button, Heading, Spacer, Stack } from '@chakra-ui/react';
import { format, formatRelative } from 'date-fns';
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { colors, styled } from '../Stitches';
import { fetchWritingDesk } from '../api/WritingDesk';
import { routes } from '../data/routes';
import wrapColorTheme from '../lib/WrapColorTheme';
import Archive from './Archive';
import { Authenticated } from './AuthGuards';
import Emoji from './Emoji';
import { Envelope } from './Envelope';
import { Fade } from './Fade';
import { Fetching } from './Fetching';
import { LetterGrid } from './LetterGrid';
import { useAuthenticateApiCall } from './hooks/useAuthenticateApiCall';
import useOpenLetter from './hooks/useOpenLetter';
import { queries } from '../data/queries';

// === LIB =====================================================================
const makeDate = (date: string): Date => new Date(date + 'Z');

const formatDateWithDay = (date: string) => format(makeDate(date), 'eeee, do LLLL yyyy');
const formatRelativeDate = (date: string) => formatRelative(makeDate(date), new Date());
const formatTitle = (title: string) => (title === '' ? 'Untitled' : title);

// === 🪝 Hooks ================================================================
const useWritingDesk = () => {
  const authedFetchWritingDesk = useAuthenticateApiCall(fetchWritingDesk);
  const { isLoading, data } = useQuery([queries.writingDesk], authedFetchWritingDesk);

  return { isLoading, data };
};

// === 🪝 Types ================================================================
export type LetterSummary = {
  id: string;
  title: string;
  sender_name: string;
  deliver_at: string;
  sender: string;
};

type DraftSummary = {
  id: string;
  title: string;
  recipient_name: string | undefined;
  temp_contact_name?: string;
  updated_at: string;
};

// === STYLED COMPONENTS ======================================================
const PlaceholderTextPrimitive = styled('p', {
  fontSize: '$2',
  fontFamily: '$mono',

  variants: { theme: { light: {}, dark: {} }, align: { center: { textAlign: 'center' } } },
});

export const PlaceholderText = wrapColorTheme(PlaceholderTextPrimitive);

const DividerPrimitive = styled('div', {
  marginTop: 4,
  height: 1,
  variants: {
    theme: {
      light: { backgroundColor: colors.gray200 },
      dark: { backgroundColor: '$ldPurpleDark' },
    },
  },
});

export const Divider = wrapColorTheme(DividerPrimitive);

// === STRUCTURAL COMPONENTS ==================================================
const NewLetter = ({ letter }: { letter: LetterSummary }) => {
  const { id, title, sender_name, deliver_at } = letter;
  const onClick = useOpenLetter(id);

  return (
    <Envelope
      onClick={onClick}
      title={formatTitle(title)}
      subLine={`From: ${sender_name}`}
      bottomLine={formatDateWithDay(deliver_at)}
    />
  );
};

const NewLetters = ({ data }: { data: LetterSummary[] }) => {
  const emoji =
    data.length === 0 ? (
      <Emoji symbol="📭" label="Emoji of an empty letter box" />
    ) : (
      <Emoji symbol="📬" label="Emoji of an open letter box with a letter inside" />
    );
  return (
    <div>
      <Heading size="lg">{emoji} Letter Box</Heading>
      <Divider />
      <Spacer height={4} />

      {data.length > 0 ? (
        <LetterGrid>
          {data.map((letter) => (
            <NewLetter key={letter.id} letter={letter} />
          ))}
        </LetterGrid>
      ) : (
        <PlaceholderText>New letters will appear here.</PlaceholderText>
      )}
    </div>
  );
};

const Draft = ({ draft }: { draft: DraftSummary }) => {
  const { id, title, recipient_name, temp_contact_name, updated_at } = draft;

  const recipient = recipient_name || temp_contact_name;
  const onClick = useOpenLetter(id, { isDraft: true });

  return (
    <Envelope
      onClick={onClick}
      title={formatTitle(title)}
      subLine={`To: ${recipient}`}
      bottomLine={`Updated ${formatRelativeDate(updated_at)}`}
    />
  );
};

const NoDraftsPlaceholder = () => (
  <Stack direction={['column', null, 'row']} align={[null, null, 'center']}>
    <PlaceholderText>You&apos;re not working on any drafts.</PlaceholderText>
    <Stack direction="row" align="center">
      <PlaceholderText>Not sure where to start?</PlaceholderText>

      <Link to={routes.newLetter}>
        <Button size={'sm'}>
          <Emoji symbol="✍️" label="Emoji of a hand holding a pen" spaceRight /> Write a letter
        </Button>
      </Link>
    </Stack>
  </Stack>
);

const Drafts = ({ data }: { data: DraftSummary[] }) => (
  <div>
    <Heading size="lg">
      <Emoji symbol="🪶" label="Emoji of a quill" /> Drafts
    </Heading>
    <Divider />
    <Spacer height={4} />
    <LetterGrid>
      {data.length > 0 ? data.map((l) => <Draft key={l.id} draft={l} />) : <NoDraftsPlaceholder />}
    </LetterGrid>
  </div>
);

// === ROOT ===================================================================
const Desk = () => {
  const { data, isLoading } = useWritingDesk();

  return (
    <Fetching isFetching={isLoading}>
      {data && (
        <Stack spacing={12}>
          <NewLetters data={data.unreadLetters as LetterSummary[]} />
          <Drafts data={data.drafts as DraftSummary[]} />
          <Archive data={data.archiveLetters} />
        </Stack>
      )}
    </Fetching>
  );
};

const Container = styled('div', {
  maxWidth: 1060,
  mx: 'auto',
  paddingInline: '$6',
  marginBottom: '$7',
});

const AuthenticatedDesk = () => (
  <Fade>
    <Authenticated>
      <Container>
        <Desk />
      </Container>
    </Authenticated>
  </Fade>
);

export default AuthenticatedDesk;
